import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminSidebar from './AdminSidebar';

import { API_BASE_URL } from '../../config';
import './AdminDashboard.css'; // Reuse common layout styles
import './AdminUsers.css';

const AdminUsers = () => {
    const { t } = useTranslation(['admin', 'common']);
    const [users, setUsers] = useState([]);

    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const recordsPerPage = 20;

    useEffect(() => {
        fetchUsers();
    }, [currentPage, searchTerm]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}get_all_users.php?page=${currentPage}&limit=${recordsPerPage}&search=${encodeURIComponent(searchTerm)}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminId')}`
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setUsers(data.users);
                setTotalUsers(data.total_users);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError(t('admin:users.errors.loadFailed', 'Failed to load users'));
        } finally {

            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on new search
    };

    const totalPages = Math.ceil(totalUsers / recordsPerPage);
    const indexOfFirstRecord = (currentPage - 1) * recordsPerPage;
    const indexOfLastRecord = Math.min(currentPage * recordsPerPage, totalUsers);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    if (loading && users.length === 0) return (
        <div className="admin-loading-screen">
            <div className="loader-inner">
                <div className="loader-orbit"></div>
                <div className="loader-text">{t('admin:users.loading', 'Loading Personnel Data...')}</div>
            </div>
        </div>

    );

    return (
        <div className={`admin-layout${sidebarOpen ? '' : ' sidebar-collapsed'}`}>
            <AdminSidebar sidebarOpen={sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} />

            <main className="admin-main">
                <header className="main-header">
                    <button
                        className="sidebar-toggle-btn"
                        onClick={() => setSidebarOpen(o => !o)}
                        aria-label="Toggle sidebar"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '42px',
                            height: '42px',
                            fontSize: '1.4rem',
                            color: '#f8fafc',
                            background: 'rgba(59,130,246,0.15)',
                            border: '1px solid rgba(59,130,246,0.4)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            padding: 0,
                        }}
                    >
                        ☰
                    </button>
                    <div className="header-search">
                        <input
                            type="text"
                            placeholder={t('admin:users.filterPlaceholder', 'Filter by name or email...')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />

                        <span className="search-icon">🔍</span>
                    </div>
                    <div className="header-actions">
                        <div className="date-display">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                </header>

                <section className="content-inner">
                    <div className="page-title-section">
                        <h1>{t('admin:users.title', 'User Management')}</h1>
                        <p>{t('admin:users.subtitle', 'View and manage all registered learners on the platform.')}</p>
                    </div>


                    {error && <div className="error-alert">{error}</div>}

                    <div className="data-panel full-width">
                        <div className="panel-header">
                            <div className="panel-info">
                                <h2>{t('admin:users.allUsers', { count: totalUsers, defaultValue: `All Users (${totalUsers})` })}</h2>

                                {totalUsers > 0 && (
                                    <p className="pagination-info">{t('admin:users.showing', { start: indexOfFirstRecord + 1, end: indexOfLastRecord, total: totalUsers, defaultValue: `Showing ${indexOfFirstRecord + 1}-${indexOfLastRecord} of ${totalUsers}` })}</p>
                                )}

                            </div>
                            <button className="export-btn" onClick={() => window.print()}>{t('admin:users.printList', 'Print List')}</button>

                        </div>

                        <div className="table-wrapper">
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th>{t('admin:users.table.studentInfo', 'Student Info')}</th>
                                        <th>{t('admin:users.table.academicDetails', 'Academic Details')}</th>
                                        <th>{t('admin:users.table.role', 'Role')}</th>
                                        <th>{t('admin:users.table.joinedDate', 'Joined Date')}</th>
                                        <th>{t('admin:users.table.actions', 'Actions')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {users.map((user, index) => (
                                        <tr key={user.id || index}>
                                            <td>
                                                <div className="user-cell">
                                                    <div className="user-initials">{user.full_name?.charAt(0)}</div>
                                                    <div className="user-info-text">
                                                        <p className="user-name">{user.full_name}</p>
                                                        <p className="user-email">{user.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="academic-info">
                                                    <p>Class: {user.student_class}</p>
                                                    <p className="stream">{user.stream || 'N/A'}</p>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`role-pill ${user.role}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button className="icon-btn edit">✏️</button>
                                                    <button className="icon-btn delete">🗑️</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {users.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan="5" className="empty-state">{t('admin:users.table.empty', 'No users found matching your search')}</td>
                                        </tr>
                                    )}

                                </tbody>
                            </table>
                             {loading && users.length > 0 && (
                                <div className="table-loading-overlay">{t('admin:users.table.updating', 'Updating...')}</div>
                            )}

                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1 || loading}
                                    className="page-btn prev"
                                >
                                    &laquo; {t('admin:pagination.previous', 'Previous')}
                                </button>

                                <div className="page-numbers">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNum = index + 1;
                                        // Show first, last, current, and pages around current
                                        if (
                                            pageNum === 1 ||
                                            pageNum === totalPages ||
                                            (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => paginate(pageNum)}
                                                    disabled={loading}
                                                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        } else if (
                                            pageNum === currentPage - 3 ||
                                            pageNum === currentPage + 3
                                        ) {
                                            return <span key={pageNum} className="pagination-ellipsis">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>
                                <button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages || loading}
                                    className="page-btn next"
                                >
                                    {t('admin:pagination.next', 'Next')} &raquo;
                                </button>

                            </div>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default AdminUsers;
